'use-client';
import * as React from 'react';
import type { ActiveAnchor, LinkProps } from '@wix/editor-elements-definitions';
import classNames from 'clsx';
import {
  formatClassNames,
  getDataAttributes,
  isEmptyObject,
  getAccessibilityAttributes,
} from '@wix/editor-elements-common-utils';
import type {
  ExpandableMenuItemWithIsSelected,
  ExpandableMenuProps,
  ExpandableMenuItem as ExpandableMenuItemType,
  IMenuItemsPropsSDKActions,
} from '../ExpandableMenu.types';
import semanticClassNames from '../ExpandableMenu.semanticClassNames';
import style from './style/ExpandableMenu.scss';
import ExpandableMenuItem from './ExpandableMenuItem';

const isAnchorLink = (link: LinkProps) =>
  !!link.anchorDataId || !!link.anchorCompId;

const removeQueryFromUrl = (url: string): string => url.split('?')[0];

const isDefaultAnchor = (activeAnchor: ActiveAnchor) =>
  activeAnchor.dataId === 'SCROLL_TO_TOP' ||
  activeAnchor.dataId === 'SCROLL_TO_BOTTOM';

const isCurrentAnchor = (activeAnchor: ActiveAnchor, link?: LinkProps) => {
  if (!link || !isAnchorLink(link)) {
    return false;
  }

  const isSameCompId =
    link.anchorCompId && link.anchorCompId === activeAnchor.compId;

  const isSameDataId =
    link.anchorDataId && link.anchorDataId === activeAnchor.dataId;

  return Boolean(isSameCompId || isSameDataId);
};

const getIsActiveItem = (
  item: ExpandableMenuItemType,
  href: string,
  activeAnchor?: ActiveAnchor,
) => {
  if (!item.link) {
    return false;
  }
  if (isAnchorLink(item.link)) {
    return (
      !!activeAnchor &&
      !isDefaultAnchor(activeAnchor) &&
      isCurrentAnchor(activeAnchor, item.link)
    );
  }
  return decodeURIComponent(removeQueryFromUrl(item.link.href || '')) === href;
};

const isFolder = (link: LinkProps | undefined) => isEmptyObject(link);

const getItemsWithIsSelected = (
  items: Array<ExpandableMenuItemType>,
  href = '',
  activeAnchor?: ActiveAnchor,
): Array<ExpandableMenuItemWithIsSelected> => {
  return items.map(item => {
    const isActiveItem =
      item.selected === false
        ? false
        : item.selected || getIsActiveItem(item, href, activeAnchor);
    const subItems = item.items || [];
    const subItemsWithIsSelected = getItemsWithIsSelected(
      subItems,
      href,
      activeAnchor,
    );
    const isActiveSubMenu = subItemsWithIsSelected.some(
      child => child.isSelected,
    );
    const isSelected =
      item.selected === false
        ? false
        : isActiveItem || (isActiveSubMenu && !isFolder(item.link));

    return {
      ...item,
      isSelected,
      items: subItemsWithIsSelected,
      isCurrent: isActiveItem,
    };
  });
};

const ExpandableMenu: React.FC<ExpandableMenuProps> = props => {
  const {
    id,
    className,
    customClassNames = [],
    items,
    currentUrl,
    translations,
    onItemClick,
    onItemDblClick,
    onItemMouseIn,
    onItemMouseOut,
    onMouseEnter,
    onMouseLeave,
    reportBiOnMenuItemClick,
    skin = 'ExpandableMenuSkin',
    applyTextSelectedColorExperiment,
    a11y,
    ariaAttributes,
    lang,
    activeAnchor,
  } = props;

  const itemsWithIsSelected = React.useMemo(
    () => getItemsWithIsSelected(items, currentUrl, activeAnchor),
    [items, currentUrl, activeAnchor],
  );

  const onMenuItemClick: IMenuItemsPropsSDKActions['onItemClick'] = (
    ...args
  ) => {
    reportBiOnMenuItemClick?.(...args);
    onItemClick?.(...args);
  };

  const a11yProps = getAccessibilityAttributes({
    ariaAttributes: { label: a11y?.label, ...ariaAttributes },
  });

  return (
    <nav
      id={id}
      {...getDataAttributes(props)}
      aria-label={
        a11yProps.ariaAttributes['aria-label'] ?? translations.ariaLabel
      }
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={classNames(
        style[skin],
        className,
        formatClassNames(semanticClassNames.root, ...customClassNames),
      )}
      lang={lang}
    >
      <ul className={style.menuContainer}>
        {itemsWithIsSelected.map((item, index) => (
          <ExpandableMenuItem
            key={index}
            {...item}
            onItemClick={onMenuItemClick}
            onItemDblClick={onItemDblClick}
            onItemMouseIn={onItemMouseIn}
            onItemMouseOut={onItemMouseOut}
            idPrefix={`${id}-${index}`}
            skin={skin}
            applyTextSelectedColorExperiment={applyTextSelectedColorExperiment}
            depth={0}
          />
        ))}
      </ul>
    </nav>
  );
};

export default ExpandableMenu;
